<template>
  <div id="app">
    <a-config-provider :getPopupContainer="getPopupContainer">
      <div class="app-provider-content">
        <internet-connection-alert v-if="showInternetConnectionAlert" />
        <app-layout>
          <modals />
          <permission-provider />
        </app-layout>
      </div>
    </a-config-provider>
  </div>
</template>

<script>
import AppLayout from '@/components/layout/app-layout.vue'
import { ConfigProvider, notification } from 'ant-design-vue'
import Modals from './components/modals.vue'
import Cookies from 'js-cookie'
import PermissionProvider from './components/permission-provider.vue'
import deepLinkMixin from './mixins/deepLinkMixin'
import apiClient from '@/services/api'
import InternetConnectionAlert from '@/components/internet-connection-alert.vue'

const VERSION_CHECK_INTERVAL = 60000
const INTERNET_CONNECTION_CHECK_INTERVAL = 60000
const UNAUTHORIZED_WEB_VERSION_CHECK_INTERVAL = 60000

export default {
  components: {
    'a-config-provider': ConfigProvider,
    'app-layout': AppLayout,
    'internet-connection-alert': InternetConnectionAlert,
    'permission-provider': PermissionProvider,
    modals: Modals
  },
  mixins: [deepLinkMixin],
  data() {
    return {
      checkingAppVersion: false,
      checkingUnauthorizedWebVersion: false,
      internetConnectionCheckIntervalId: null,
      unauthorizedWebVersionCheckIntervalId: null,
      lastAppVersionCheckAt: 0
    }
  },
  computed: {
    hasAuthToken() {
      return Boolean(this.$store.state.applicationState.token || Cookies.get('access'))
    },
    showInternetConnectionAlert() {
      const internetConnection = this.$store.state.applicationState.internetConnection

      return internetConnection?.stable === false && !internetConnection?.dismissed
    }
  },
  watch: {
    '$store.state.applicationState.currentProject.id': {
      async handler(newVal, oldVal) {
        if (!newVal) {
          Cookies.remove('project_id')
        } else {
          Cookies.set('project_id', newVal)
        }

        if (oldVal != null && newVal != null && String(oldVal) !== String(newVal)) {
          await this.$router.replace({ name: 'home', query: {} }).catch(() => {})
        }
      },
      deep: true,
      immediate: true
    },
    '$store.state.applicationState.associate.associate_number': {
      handler(newVal) {
        if (!newVal) {
          Cookies.remove('associate_number')
        } else {
          Cookies.set('associate_number', newVal)
        }
      },
      deep: true,
      immediate: true
    },
    '$store.state.applicationState.needUpdate': {
      async handler(newVal) {
        if (newVal) {
          await this.$store.dispatch('loadApplicationState')
          await this.$router.replace({ query: {} }).catch(() => {})

          this.$store.commit('setNeedUpdate', false)

          notification.info({
            message: 'Refresh the page',
            description: 'ePeople application data is unsynchronised. Please refresh the page to resync!'
          })
        }
      }
    },
    hasAuthToken: {
      handler(newVal) {
        if (newVal) {
          this.stopUnauthorizedWebVersionMonitor()
        } else {
          this.startUnauthorizedWebVersionMonitor()
        }
      },
      immediate: true
    }
  },
  methods: {
    getPopupContainer(el, dialogContext) {
      if (dialogContext) {
        return dialogContext.getDialogWrap()
      } else {
        return document.body
      }
    },

    async checkAppVersionOnTabActive() {
      if (document.visibilityState === 'hidden' || this.checkingAppVersion) {
        return
      }

      const now = Date.now()

      if (now - this.lastAppVersionCheckAt < VERSION_CHECK_INTERVAL) {
        return
      }

      this.lastAppVersionCheckAt = now
      this.checkingAppVersion = true

      try {
        await apiClient.get('/api/healthcheck', {
          not_required: true,
          skipAuthRefresh: true
        })
      } catch (_) {
      } finally {
        this.checkingAppVersion = false
      }
    },

    async checkUnauthorizedWebVersion() {
      if (this.hasAuthToken || this.checkingUnauthorizedWebVersion) {
        return
      }

      this.checkingUnauthorizedWebVersion = true

      try {
        await apiClient.get('/api/web_version', {
          not_required: true,
          skipAuthRefresh: true
        })
      } catch (_) {
      } finally {
        this.checkingUnauthorizedWebVersion = false
      }
    },

    startUnauthorizedWebVersionMonitor() {
      if (this.hasAuthToken || this.unauthorizedWebVersionCheckIntervalId) {
        return
      }

      this.checkUnauthorizedWebVersion()
      this.unauthorizedWebVersionCheckIntervalId = setInterval(
        this.checkUnauthorizedWebVersion,
        UNAUTHORIZED_WEB_VERSION_CHECK_INTERVAL
      )
    },

    stopUnauthorizedWebVersionMonitor() {
      if (this.unauthorizedWebVersionCheckIntervalId) {
        clearInterval(this.unauthorizedWebVersionCheckIntervalId)
        this.unauthorizedWebVersionCheckIntervalId = null
      }
    },

    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.checkAppVersionOnTabActive()
        this.checkInternetConnectionStatus()
      }
    },

    async checkInternetConnectionStatus() {
      await this.$store.dispatch('checkInternetConnection')
    },

    startInternetConnectionMonitor() {
      this.checkInternetConnectionStatus()
      this.internetConnectionCheckIntervalId = setInterval(
        this.checkInternetConnectionStatus,
        INTERNET_CONNECTION_CHECK_INTERVAL
      )
    },

    stopInternetConnectionMonitor() {
      if (this.internetConnectionCheckIntervalId) {
        clearInterval(this.internetConnectionCheckIntervalId)
        this.internetConnectionCheckIntervalId = null
      }
    }
  },
  mounted() {
    const saved = localStorage.getItem('app-theme')
    document.body.className = saved || 'theme-light'

    window.addEventListener('focus', this.checkAppVersionOnTabActive)
    window.addEventListener('focus', this.checkInternetConnectionStatus)
    window.addEventListener('online', this.checkInternetConnectionStatus)
    window.addEventListener('offline', this.checkInternetConnectionStatus)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.startInternetConnectionMonitor()
  },
  beforeDestroy() {
    window.removeEventListener('focus', this.checkAppVersionOnTabActive)
    window.removeEventListener('focus', this.checkInternetConnectionStatus)
    window.removeEventListener('online', this.checkInternetConnectionStatus)
    window.removeEventListener('offline', this.checkInternetConnectionStatus)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.stopUnauthorizedWebVersionMonitor()
    this.stopInternetConnectionMonitor()
  }
}
</script>

<style scoped>
.app-provider-content {
  height: 100%;
}
</style>
